import { __awaiter } from '../../../../../_virtual/_tslib.js';
import { useState, useEffect } from 'react';
import { getWalletConnectorByName, isSameAddress } from '@dynamic-labs/multi-wallet';
import '@dynamic-labs/sdk-api';
import '../../../shared/logger.js';
import '@dynamic-labs/iconic';
import 'react/jsx-runtime';
import '../../../context/ViewContext/ViewContext.js';
import { CONNECTED_WALLET_CONNECTOR_DATA } from '../../constants/localStorage.js';
import '../../constants/colors.js';
import { useLocalStorage } from '../../../shared/utils/hooks/useLocalStorage/useLocalStorage.js';
import '../../../shared/consts/index.js';
import { getWalletProvider } from '../../functions/getWalletProvider/getWalletProvider.js';
import { createVisit } from '../../../data/api.js';
import { shouldManuallyReconnectOnRefresh } from '../../functions/shouldManuallyReconnectOnRefresh/shouldManuallyReconnectOnRefresh.js';

const useConnectWallet = (wallets, authMode, environmentId, enableVisitTrackingOnConnectOnly) => {
    const [connectedInfo, setConnectedInfo, removeConnectedInfo] = useLocalStorage(CONNECTED_WALLET_CONNECTOR_DATA, null);
    const [connectedWallet, setConnectedWallet] = useState(null);
    useEffect(() => {
        const fetchBasicInfo = (connectedWallet) => __awaiter(void 0, void 0, void 0, function* () {
            const provider = getWalletProvider(connectedWallet.connector);
            setConnectedInfo({
                chain: connectedWallet.chain,
                name: connectedWallet.connector.name,
                provider,
                walletPublicKey: connectedWallet.address,
            });
        });
        if (connectedWallet) {
            fetchBasicInfo(connectedWallet);
        }
    }, [connectedWallet === null || connectedWallet === void 0 ? void 0 : connectedWallet.address]);
    const connectWalletByAddressAndChain = (name, address, chain) => __awaiter(void 0, void 0, void 0, function* () {
        const walletConnector = getWalletConnectorByName(wallets.map((wallet) => wallet.walletConnector), name);
        if (walletConnector) {
            // this is here because phantom fires a disconnect event on page load,
            // and other wallets and chains have similar issues
            if (shouldManuallyReconnectOnRefresh(walletConnector)) {
                yield walletConnector.connect();
            }
            const connectedAccounts = yield walletConnector.getConnectedAccounts();
            const result = connectedAccounts.filter((account) => isSameAddress(account, address, chain));
            if (result.length > 0) {
                walletConnector.connect();
                setConnectedWallet({
                    address,
                    chain,
                    connected: true,
                    connector: walletConnector,
                    id: 'connect-only-wallet',
                });
            }
            else {
                setConnectedWallet(null);
            }
        }
        else {
            setConnectedWallet(null);
        }
    });
    useEffect(() => {
        if (authMode === 'connect-only' && connectedInfo) {
            if (connectedInfo.walletPublicKey &&
                connectedInfo.name &&
                connectedInfo.chain) {
                const { name, walletPublicKey, chain } = connectedInfo;
                connectWalletByAddressAndChain(name, walletPublicKey, chain);
            }
        }
    }, [connectedInfo, wallets.length]);
    const connectWallet = (walletConnector, fetchPublicAddressOpts) => __awaiter(void 0, void 0, void 0, function* () {
        const publicWalletAddress = yield walletConnector.fetchPublicAddress(fetchPublicAddressOpts);
        if (!publicWalletAddress) {
            const error = new Error();
            error.code = 'missing-public-address';
            throw error;
        }
        const chain = walletConnector.connectedChain || '';
        setConnectedWallet({
            address: publicWalletAddress,
            chain,
            connected: true,
            connector: walletConnector,
            id: 'connect-only-wallet',
        });
        if (authMode === 'connect-only' && !enableVisitTrackingOnConnectOnly) {
            return publicWalletAddress;
        }
        // send information to backend to kick off background jobs
        // so verify/sign on the next step could go by more quickly
        // this is async work, but DO NOT AWAIT
        createVisit({
            authMode,
            chain,
            environmentId,
            publicWalletAddress,
            walletName: walletConnector.name,
            walletProvider: getWalletProvider(walletConnector),
        });
        return publicWalletAddress;
    });
    return {
        connectWallet,
        connectWalletByAddressAndChain,
        connectedInfo,
        connectedWallet,
        removeConnectedInfo,
        setConnectedInfo,
        setConnectedWallet,
    };
};

export { useConnectWallet };
